import type { JSX } from 'react';
import { ACHIEVEMENTS, freezesHeld, levelForXp, MAX_STREAK_FREEZES, streakStatus, xpForLevel } from '@yuan-math/learning';
import { Card, MasteryBar, SectionHeading, Stat } from '@yuan-math/ui';
import { DailyQuests } from '../components/DailyQuests.tsx';
import { useLearner } from '../state/LearnerContext.tsx';

/**
 * Achievements.
 *
 * Present because habits matter, and kept firmly in their place: this page
 * opens by saying what the badges are not (§10). Everything here — XP, levels,
 * quests, streaks, rest days — is about *showing up*. Nothing on this page
 * unlocks a single piece of mathematics, and the page says so.
 */
export function AchievementsPage(): JSX.Element {
  const { profile, quests, weekly, today, setWeeklyGoalDays } = useLearner();
  if (!profile) return <div />;

  const owned = new Map(profile.achievements.map((entry) => [entry.id, entry.unlockedAt]));
  const level = levelForXp(profile.xp);
  const now = Date.now();
  const status = streakStatus(profile.streak, now);
  const restDays = freezesHeld(profile.streak);
  const untilRest = 5 - (profile.streak.current % 5 || 5);

  return (
    <div className="page page--wide">
      <header className="page-header">
        <h1 className="page__title">Achievements</h1>
        <p className="page__lead">
          Badges, quests and streaks reward showing up and sticking with it. They are not a measurement of how much
          mathematics you know — that is what <strong>mastery</strong> is for.
        </p>
      </header>

      <Card className="level-card">
        <div className="level-card__head">
          <div>
            <p className="level-card__level">Level {level.level}</p>
            <p className="level-card__title">{level.title}</p>
          </div>
          <p className="level-card__xp">{profile.xp} XP</p>
        </div>
        <MasteryBar value={level.fraction} showValue={false} />
        <p className="level-card__next">
          {level.xpForNextLevel - level.xpIntoLevel} XP to level {level.level + 1} · level {level.level + 2} begins at{' '}
          {xpForLevel(level.level + 2)} XP
        </p>
      </Card>

      <DailyQuests quests={quests} weekly={weekly} heading="Today" />

      <section className="dashboard-section">
        <SectionHeading
          title="Your streak"
          description="A run of days with at least one question answered. It is meant to be kept, not feared."
        />
        <Card className="streak-card">
          <div className="streak-card__stats">
            <Stat label="Current" value={`${profile.streak.current}`} hint={streakNote(status)} />
            <Stat label="Longest" value={`${profile.streak.longest}`} hint="days in a row" />
            <Stat label="Days practised" value={`${profile.streak.totalActiveDays}`} hint="all time" />
            <Stat label="Best run" value={`${profile.stats.bestCombo ?? 0}`} hint="correct answers in a row" />
          </div>

          <div className="streak-card__rest">
            <p className="streak-card__rest-title">
              Rest days banked: {restDays} of {MAX_STREAK_FREEZES}{' '}
              <span aria-hidden="true">{restDays > 0 ? '❄️'.repeat(restDays) : ''}</span>
            </p>
            <p className="streak-card__rest-note">
              You bank one rest day for every five days you practise, and hold at most {MAX_STREAK_FREEZES}. If you
              miss a day, one is spent automatically and your streak carries on. Illness, holidays and bad weeks are
              not mathematical failures.
              {restDays < MAX_STREAK_FREEZES && profile.streak.current > 0
                ? ` ${untilRest} more ${untilRest === 1 ? 'day' : 'days'} earns the next one.`
                : ''}
            </p>
          </div>
        </Card>
      </section>

      <section className="dashboard-section">
        <SectionHeading
          title="Your weekly goal"
          description="How many days a week you are aiming for. Your number — change it whenever it stops fitting."
        />
        <Card className="goal-card">
          <div className="goal-card__choices" role="group" aria-label="Days a week">
            {[2, 3, 4, 5, 6, 7].map((days) => (
              <button
                key={days}
                type="button"
                className={`goal-choice ${weekly.target === days ? 'goal-choice--selected' : ''}`}
                aria-pressed={weekly.target === days}
                onClick={() => setWeeklyGoalDays(days)}
              >
                {days}
              </button>
            ))}
          </div>
          <p className="goal-card__note">
            {weekly.daysActive} of the last seven days had practice in them. Aiming lower and hitting it beats aiming
            higher and missing.
          </p>
        </Card>
      </section>

      <section className="dashboard-section">
        <SectionHeading
          title={`Badges (${owned.size} of ${ACHIEVEMENTS.length})`}
          description={
            today.attempts > 0
              ? `${today.attempts} questions answered today.`
              : 'Earned by practising — never bought, and never lost once earned.'
          }
        />
        <ul className="achievement-grid">
          {ACHIEVEMENTS.map((achievement) => {
            const unlockedAt = owned.get(achievement.id);
            const earned = unlockedAt !== undefined;
            return (
              <li key={achievement.id}>
                <Card className={`achievement ${earned ? 'achievement--earned' : 'achievement--locked'}`}>
                  <span className="achievement__icon" aria-hidden="true">
                    {achievement.icon}
                  </span>
                  <div>
                    <p className="achievement__title">{achievement.title}</p>
                    <p className="achievement__description">{achievement.description}</p>
                    {earned ? (
                      <p className="achievement__date">Earned {new Date(unlockedAt).toLocaleDateString()}</p>
                    ) : null}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

function streakNote(status: ReturnType<typeof streakStatus>): string {
  switch (status) {
    case 'safe':
      return 'today is counted';
    case 'at-risk':
      return 'practise today to keep it';
    case 'rescuable':
      return 'a rest day is holding it';
    case 'broken':
      return 'ready for a new one';
    default:
      return 'not started';
  }
}
